import { formatDateOnly, formatTimestampDate } from '../dates';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { saveRequest } from '../ui/save-request';
import { Link } from '../ui/ScopedLink';
import { useStaffSession, useScopePending } from '../ui/StaffSession';
import { PageHeader } from '../ui/PageHeader';
import { ErrorState, Loading, Empty, reviewedApplicationError } from '../ui/State';
import { CommandForm, Choices, Evidence, type Field } from './ConsultationForms';
import { ResponsiveTable } from '../ui/ResponsiveTable';
type Row = Record<string, any>;
type Kind = 'Section 20' | 'Section 20B';
const base = (kind: Kind) => (kind === 'Section 20' ? '/work/consultations' : '/finance/service-charges/s20b-consultations');
export function Consultations({ kind = 'Section 20' }: { kind?: Kind }) {
  const session = useStaffSession(),
    pending = useScopePending(),
    building = session?.effectiveScope.kind === 'building' ? session.effectiveScope : undefined;
  const { consultationId } = useParams();
  return building && !pending ? (
    <ConsultationPage key={kind + ':' + building.id + ':' + (consultationId ?? 'register')} kind={kind} building={building} id={consultationId} />
  ) : (
    <Empty>
      Choose a building to open {kind} consultations. <Link to="/buildings">Open buildings</Link>
    </Empty>
  );
}
function ConsultationPage({ building, kind, id }: { building: { id: string; name: string }; kind: Kind; id?: string | undefined }) {
  const [search] = useSearchParams(),
    workFilter = search.get('work');
  const root = `/api/buildings/${building.id}/consultations`,
    query = `?building=${building.id}`;
  const [setup, setSetup] = useState<Row>(),
    [data, setData] = useState<Row>(),
    [error, setError] = useState<unknown>(),
    [refresh, setRefresh] = useState(0),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [page, setPage] = useState(1),
    [status, setStatus] = useState('');
  useEffect(() => {
    let active = true;
    setError(undefined);
    Promise.all([
      api<Row>(root + `/setup${query}&kind=${encodeURIComponent(kind)}`),
      api<Row>(id ? root + '/' + id + query : root + query + `&kind=${encodeURIComponent(kind)}&page=${page}${workFilter ? '&workId=' + encodeURIComponent(workFilter) : ''}${status ? '&status=' + status : ''}`)
    ])
      .then(([s, d]) => {
        if (s.building !== building.id || d.building !== building.id || (id && d.row.kind !== kind)) throw reviewedApplicationError('The consultation does not match this building and register.');
        if (active) {
          setSetup(s);
          setData(d);
        }
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [refresh, page, status, workFilter]);
  async function save(path: string, body: Row) {
    setBusy(true);
    setError(undefined);
    setMessage('');
    try {
      const r = await saveRequest<Row>(root + path + query, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (r.building !== building.id) throw reviewedApplicationError('The saved consultation does not match this building.');
      setMessage('Saved.');
      setRefresh((v) => v + 1);
      return r;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  }
  if (!setup || !data) return error ? <ErrorState error={error} onRetry={() => setRefresh((v) => v + 1)} /> : <Loading />;
  return (
    <div className="demand-workflow">
      <PageHeader
        title={id ? data.row.title : kind === 'Section 20' ? 'Section 20 consultations' : 'Section 20B notice reviews'}
        scope={{ kind: 'building', name: building.name }}
        context="Prepare, review and retain notices and service evidence. Consultation review does not appoint a supplier or issue a demand."
      />
      {kind === 'Section 20B' && (
        <p>
          <Link to="/finance/service-charges/s20b-notices">Costs and recorded Section 20B notices</Link>
        </p>
      )}
      {message && <p role="status">{message}</p>}
      {Boolean(error) && <ErrorState error={error} />}
      {id ? (
        <ConsultationDetail key={`${id}:${data.row.version}`} data={data} setup={setup} busy={busy} kind={kind} save={(body) => save('/' + id + '/commands', body)} />
      ) : (
        <>
          <section>
            <h2>Consultations</h2>
            {workFilter && (
              <p>
                Showing consultations for the selected Work. <Link to={base(kind)}>Show all consultations</Link>
              </p>
            )}
            <label>
              Status
              <select
                aria-label="Status"
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
              >
                <option value="">All statuses</option>
                {['Open', 'Completed', 'Cancelled'].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            {data.rows.length ? (
              <ResponsiveTable>
                <thead>
                  <tr>
                    <th>Consultation</th>
                    <th>Owner</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r: Row) => (
                    <tr key={r.id}>
                      <td>
                        <Link to={base(kind) + '/' + r.id}>{r.title}</Link>
                      </td>
                      <td>{r.owner_name}</td>
                      <td>{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            ) : (
              <Empty>No consultations recorded.</Empty>
            )}
            <p>
              Page {page}
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <button disabled={page * 50 >= data.total} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </p>
          </section>
          {setup.permissions.manage && (
            <details>
              <summary>Start {kind === 'Section 20' ? 'a consultation' : 'a notice review'}</summary>
              <FactsForm kind={kind} setup={setup} busy={busy} onSave={(body) => save('', body)} />
            </details>
          )}
          {kind === 'Section 20B' && setup.permissions.approve && (
            <CommandForm
              title="Review the actual incurred date of a cost"
              busy={busy}
              docs={setup.documents}
              fields={[
                {
                  name: 'payableEntryId',
                  label: 'Posted cost',
                  options: setup.costs.map((c: Row) => [c.id, `${c.supplier_name} ${c.invoice_number} — invoice ${formatDateOnly(c.invoice_date)}; incurred ${formatDateOnly(c.s20b_incurred_on)}`])
                },
                {
                  name: 'incurredOn',
                  label: 'Reviewed actual incurred date',
                  type: 'date'
                },
                {
                  name: 'basis',
                  label: 'Legal and factual basis for this date',
                  type: 'textarea'
                }
              ]}
              onSave={(values, key) =>
                save('/incurred-review', {
                  ...values,
                  expectedReviewId: setup.costs.find((c: Row) => c.id === values.payableEntryId)?.incurred_review_id ?? null,
                  idempotencyKey: key
                })
              }
            />
          )}
          {kind === 'Section 20B' && setup.permissions.approve && (
            <CommandForm
              title="Deadline reminders"
              busy={busy}
              docs={[]}
              fields={[
                {
                  name: 'ownerId',
                  label: 'Deadline owner',
                  options: setup.users.map((u: Row) => [u.id, u.display_name])
                },
                {
                  name: 'enabled',
                  label: 'Enable in-app reminders',
                  type: 'checkbox',
                  value: setup.monitor?.enabled ?? false
                }
              ]}
              onSave={(values, key) =>
                save('/monitor', {
                  ownerId: values.ownerId,
                  enabled: values.enabled,
                  expectedVersion: setup.monitor?.version ?? 0,
                  reason: values.reason,
                  idempotencyKey: key
                })
              }
            />
          )}
        </>
      )}
    </div>
  );
}
type Save = (body: Row) => Promise<Row>;
function FactsForm({ kind, setup, busy, onSave, initial }: { kind: Kind; setup: Row; busy: boolean; onSave: Save; initial?: Row }) {
  const [search] = useSearchParams(),
    [workId, setWorkId] = useState(initial?.work_item_id ?? search.get('work') ?? ''),
    [variationId, setVariationId] = useState(initial?.facts.variationId ?? search.get('variation') ?? ''),
    [route, setRoute] = useState(initial?.facts.route ?? (kind === 'Section 20B' ? 'Section 20B costs' : 'England private works'));
  const [recipients, setRecipients] = useState<Row[]>(initial?.facts.recipients ?? []),
    [key, setKey] = useState(() => crypto.randomUUID());
  const f = initial?.facts,
    work = setup.works.find((w: Row) => w.id === workId);
  function addProperty(id: string) {
    const p = setup.properties.find((p: Row) => p.id === id),
      known = setup.recipientSources.find((p: Row) => p.premisesId === id)?.recipient;
    if (!p) return;
    setRecipients((old) => [
      ...old,
      {
        key: crypto.randomUUID(),
        premisesId: id,
        name: known?.official_owner_name ?? '',
        address: known?.correspondence_address ?? '',
        association: false,
        method: 'Post',
        authority: ''
      }
    ]);
  }
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = Object.fromEntries(new FormData(e.currentTarget)),
      documents = new FormData(e.currentTarget).getAll('documentIds');
    const facts = {
      route,
      variationId: variationId || null,
      jurisdiction: String(v.jurisdiction),
      arrangement: String(v.arrangement ?? 'Works'),
      agreementMonths: Number(v.agreementMonths ?? 12),
      gross: String(v.gross ?? '0.00'),
      maximumContribution: String(v.maximumContribution ?? '0.00'),
      contributionBasis: String(v.contributionBasis ?? 'Individual contributions remain subject to the reviewed leases'),
      landlord: String(v.landlord),
      reasons: String(v.reasons ?? 'Notification of the listed posted costs already incurred'),
      inspection: String(v.inspection ?? 'Not applicable to a costs notification'),
      observationsAddress: String(v.observationsAddress ?? 'Not applicable to a costs notification'),
      routeBasis: String(v.routeBasis),
      recipients,
      payableEntryIds: new FormData(e.currentTarget).getAll('costIds'),
      externalStages: route === 'Reviewed external route' ? new FormData(e.currentTarget).getAll('externalStage') : []
    };
    try {
      await onSave(
        initial
          ? {
              action: 'Revise',
              version: initial.version,
              facts,
              reason: v.reason,
              documentIds: documents,
              idempotencyKey: key
            }
          : {
              kind,
              title: kind === 'Section 20' ? work?.title : String(v.title),
              workId: kind === 'Section 20' ? workId : null,
              ownerId: String(v.ownerId),
              facts,
              reason: v.reason,
              documentIds: documents,
              idempotencyKey: key
            }
      );
      setKey(crypto.randomUUID());
    } catch {
      /* Parent retains the error and entered values. */
    }
  }
  const input = (name: string, label: string, value?: any, type = 'text') => (
    <label>
      {label}
      <input name={name} type={type} defaultValue={value ?? f?.[name] ?? ''} required />
    </label>
  );
  const area = (name: string, label: string, value?: any) => (
    <label>
      {label}
      <textarea name={name} defaultValue={value ?? f?.[name] ?? ''} required minLength={3} />
    </label>
  );
  return (
    <form aria-label="Consultation facts" onSubmit={submit}>
      <fieldset disabled={busy}>
        <legend>{initial ? 'Revise the retained facts' : 'Consultation details'}</legend>
        {kind === 'Section 20' ? (
          <label>
            Work record
            <select
              aria-label="Work record"
              value={workId}
              required
              disabled={Boolean(initial)}
              onChange={(e) => {
                setWorkId(e.target.value);
                setVariationId('');
              }}
            >
              <option value="">Choose work</option>
              {setup.works.map((w: Row) => (
                <option key={w.id} value={w.id}>
                  {w.title}
                </option>
              ))}
            </select>
          </label>
        ) : (
          input('title', 'Notice review title', initial?.title)
        )}
        {work && (
          <>
            <label>
              Scope to consult on
              <select aria-label="Scope to consult on" value={variationId} onChange={(e) => setVariationId(e.target.value)}>
                <option value="">Current Work scope</option>
                {(setup.variations ?? [])
                  .filter((v: Row) => v.work_item_id === workId)
                  .map((v: Row) => (
                    <option key={v.id} value={v.id}>
                      Variation: {v.revised_scope}
                    </option>
                  ))}
              </select>
            </label>
            <p>{setup.variations?.find((v: Row) => v.id === variationId)?.revised_scope ?? work.scope_text}</p>
          </>
        )}
        {!initial && (
          <label>
            Consultation owner
            <select aria-label="Consultation owner" name="ownerId" required>
              <option value="">Choose owner</option>
              {setup.users.map((u: Row) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Jurisdiction
          <select aria-label="Jurisdiction" name="jurisdiction" defaultValue={f?.jurisdiction ?? 'England'}>
            <option>England</option>
            <option>Wales</option>
          </select>
        </label>
        {kind === 'Section 20' && (
          <>
            <label>
              Arrangement
              <select aria-label="Arrangement" name="arrangement" defaultValue={f?.arrangement ?? 'Works'}>
                <option>Works</option>
                <option>Long-term agreement</option>
                <option>Works under long-term agreement</option>
              </select>
            </label>
            <label>
              Consultation route
              <select aria-label="Consultation route" value={route} onChange={(e) => setRoute(e.target.value)}>
                <option>England private works</option>
                <option>England private long-term agreement</option>
                <option>England works under long-term agreement</option>
                <option>Reviewed external route</option>
              </select>
            </label>
            {route === 'Reviewed external route' && (
              <>
                <p>For public-notice or other specialist routes, retain the reviewed requirements and final externally prepared text. No private-sector template is substituted.</p>
                {['Intention', 'Estimates', 'Award'].map((s) => (
                  <label key={s}>
                    <input type="checkbox" name="externalStage" value={s} defaultChecked={f?.externalStages?.includes(s)} /> {s} notice required
                  </label>
                ))}
              </>
            )}
            {input('agreementMonths', 'Agreement duration (months)', f?.agreementMonths ?? 12, 'number')}
            {input('gross', 'Gross works cost or total commitment (£)')}
            {input('maximumContribution', 'Highest individual contribution (£; annual for a long-term agreement)')}
            {area('contributionBasis', 'Contribution calculation and source')}
            {area('reasons', 'Why the works or services are proposed')}
            {area('inspection', 'Where and when descriptions and estimates can be inspected free of charge')}
            {area('observationsAddress', 'Where written observations should be sent')}
          </>
        )}
        {area('landlord', 'Landlord legal name')}
        {area('routeBasis', kind === 'Section 20' ? 'Reviewed route, authority and any tenant association requirements' : 'Lease liability, landlord authority and service review')}
        {kind === 'Section 20B' && (
          <Choices
            name="costIds"
            title="Posted costs covered by this notice"
            options={(setup.costs ?? []).map((c: Row) => ({
              id: c.id,
              title: `${c.supplier_name} — ${c.invoice_number} — ${formatDateOnly(c.invoice_date)} — £${c.gross_amount}`
            }))}
            initial={f?.payableEntryIds ?? search.get('costs')?.split(',') ?? []}
          />
        )}
      </fieldset>
      <fieldset disabled={busy}>
        <legend>Recipients — review names, addresses and service authority</legend>
        <label>
          Add a property
          <select aria-label="Add a property" value="" onChange={(e) => addProperty(e.target.value)}>
            <option value="">Choose property</option>
            {setup.properties.map((p: Row) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </label>
        {kind === 'Section 20' && (
          <button
            type="button"
            onClick={() =>
              setRecipients((old) => [
                ...old,
                {
                  key: crypto.randomUUID(),
                  premisesId: null,
                  name: '',
                  address: '',
                  association: true,
                  method: 'Post',
                  authority: ''
                }
              ])
            }
          >
            Add recognised tenants’ association
          </button>
        )}
        {recipients.map((r, i) => {
          const update = (field: string, value: string) => setRecipients((old) => old.map((x, n) => (n === i ? { ...x, [field]: value } : x)));
          return (
            <fieldset key={r.key}>
              <legend>{r.association ? 'Recognised tenants’ association' : (setup.properties.find((p: Row) => p.id === r.premisesId)?.display_name ?? 'Recipient')}</legend>
              <label>
                Legal recipient name
                <input required minLength={3} value={r.name} onChange={(e) => update('name', e.target.value)} />
              </label>
              <label>
                Service address
                <textarea required minLength={3} value={r.address} onChange={(e) => update('address', e.target.value)} />
              </label>
              <label>
                Service method
                <select value={r.method} onChange={(e) => update('method', e.target.value)}>
                  {['Post', 'Email', 'Personal service', 'Other'].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </label>
              <label>
                Service authority and address review
                <textarea value={r.authority} onChange={(e) => update('authority', e.target.value)} required={r.method === 'Email'} />
              </label>
              <button type="button" onClick={() => setRecipients((old) => old.filter((_, n) => n !== i))}>
                Remove recipient
              </button>
            </fieldset>
          );
        })}
      </fieldset>
      <Evidence documents={setup.documents} />
      {area('reason', 'Reason for this record', '')}
      <button disabled={busy || !recipients.length}>{initial ? 'Save revised facts' : 'Start review'}</button>
    </form>
  );
}
function ConsultationDetail({ data, setup, busy, kind, save }: { data: Row; setup: Row; busy: boolean; kind: Kind; save: Save }) {
  const row = data.row,
    events = data.events as Row[],
    last = events.map((e) => e.event_type).lastIndexOf('Revise'),
    active = last < 0 ? events : events.slice(last + 1),
    map = new Map<string, Row>();
  for (const e of active) if (e.event_type === 'Notice') map.set(e.payload.stage, e);
  const notices = [...map.values()];
  const estimates = new Map<string, Row>();
  for (const e of active) if (e.event_type === 'Estimate') estimates.set(e.payload.contractorId, e);
  const estimateOptions = [...estimates.values()].map((e) => [e.id, `${e.payload.supplierName} � �${e.payload.gross}`]);
  const form = (action: string, title: string, fields: Field[], approve = false) =>
    setup.permissions[approve ? 'approve' : 'manage'] && (row.status === 'Open' || (['Withdraw service', 'Reopen'].includes(action) && row.status === 'Completed')) ? (
      <CommandForm
        key={action}
        recipients={row.facts.recipients}
        title={title}
        busy={busy}
        docs={setup.documents}
        fields={fields}
        onSave={(values, key) =>
          save({
            ...values,
            ...(action === 'Notice' ? { body: values.body ?? '' } : action === 'Observation' ? { response: values.response ?? '' } : {}),
            action,
            version: row.version,
            idempotencyKey: key
          })
        }
      />
    ) : null;
  const select = (name: string, label: string, options: any[][], optional = false): Field => ({ name, label, options, optional }),
    field = (name: string, label: string, type = 'textarea', optional = false): Field => ({ name, label, type, optional });
  const noticeOptions = notices.map((n) => [n.id, `${n.payload.stage} — ${formatDateOnly(n.payload.noticeDate)}`]),
    recipientOptions = row.facts.recipients.map((r: Row) => [r.key, r.name]),
    supplierOptions = setup.suppliers.map((s: Row) => [s.id, s.legal_name]);
  return (
    <>
      <p>
        <Link to={base(kind)}>Back to register</Link>
        {row.work_item_id && (
          <>
            {' '}
            · <Link to={`/work/cases/${row.work_item_id}/maintenance`}>Open Work and procurement</Link>
          </>
        )}
      </p>
      <p>
        Status: <strong>{row.status}</strong> · Owner: {setup.users.find((u: Row) => u.id === row.owner_id)?.display_name ?? 'Review owner'}
      </p>
      {data.sourceChanged && <p role="alert">The Work, costs, incurred-date review or recipient sources changed. Revise the retained facts and review the notices again.</p>}
      <section>
        <h2>Next actions</h2>
        {kind === 'Section 20' && (
          <>
            <h3>Before appointment</h3>
            {data.appointmentReadiness?.missing.length ? (
              <ul>
                {data.appointmentReadiness.missing.map((s: string) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            ) : (
              <p>Pre-appointment evidence is ready for authorised clearance. Record the actual award and any required award notices afterwards.</p>
            )}
            <h3>Complete the consultation file</h3>
          </>
        )}
        {data.readiness.missing.length ? (
          <ul>
            {data.readiness.missing.map((s: string) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        ) : (
          <p>Ready for the final authorised review. No supplier appointment or demand is created here.</p>
        )}
      </section>
      <details>
        <summary>Retained facts and recipients</summary>
        <p>
          {row.facts.landlord} · {row.facts.route}
        </p>
        <p>{row.source_snapshot.scope ?? row.facts.reasons}</p>
        {kind === 'Section 20' && (
          <p>
            Commitment: £{row.facts.gross}; highest contribution: £{row.facts.maximumContribution}
          </p>
        )}
        {row.facts.recipients.map((r: Row) => (
          <p key={r.key}>
            {r.name} — {r.address} — {r.method}
          </p>
        ))}
      </details>
      {row.status === 'Open' && setup.permissions.approve && (
        <details>
          <summary>Revise facts, scope or recipients</summary>
          <p>A revision retains the history and requires fresh notices, approvals and service.</p>
          <FactsForm kind={kind} setup={setup} busy={busy} initial={row} onSave={save} />
        </details>
      )}
      {form(
        'Assign',
        'Change consultation owner',
        [
          select(
            'ownerId',
            'Owner',
            setup.users.map((u: Row) => [u.id, u.display_name])
          )
        ],
        true
      )}
      {form('Notice', 'Prepare or revise a notice', [
        select(
          'stage',
          'Notice stage',
          (kind === 'Section 20B' ? ['Costs incurred'] : row.facts.arrangement === 'Works under long-term agreement' ? ['Intention'] : ['Intention', 'Estimates', 'Award']).map((s) => [s, s])
        ),
        field('noticeDate', 'Proposed notice date', 'date'),
        field('effectiveDate', 'Effective date after reviewing service timing', 'date'),
        field('deadline', 'Observations deadline (at least 30 days later; blank for award or costs notices)', 'date', true),
        field('body', 'Reviewed text (leave blank to prepare text for an England private route)', 'textarea', true),
        {
          name: 'landlordApprovedText',
          label: 'External route text reviewed for this exact stage',
          type: 'checkbox'
        }
      ])}
      {notices.map((n) => (
        <section key={n.id}>
          <h2>{n.payload.stage} notice</h2>
          <p>
            {formatDateOnly(n.payload.noticeDate)} {n.payload.deadline && `· Observations by ${formatDateOnly(n.payload.deadline)}`}
          </p>
          <details>
            <summary>Read retained notice text</summary>
            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.payload.body}</div>
          </details>
          <p>
            {active.some((e) => e.event_type === 'Approve' && e.payload.noticeId === n.id) ? 'Approved version retained' : 'Awaiting approval'} ·{' '}
            {active.filter((e) => e.event_type === 'Service' && e.payload.noticeId === n.id && e.payload.result === 'Served' && !active.some((w) => w.event_type === 'Withdraw service' && w.payload.serviceId === e.id)).length} successful
            recipient service records
          </p>
        </section>
      ))}
      {form('Approve', 'Approve the final notice version', [select('noticeId', 'Notice', noticeOptions)], true)}
      {form('Service', 'Record actual service or a failed attempt', [
        select('noticeId', 'Approved notice', noticeOptions),
        select('recipientKey', 'Recipient', recipientOptions),
        field('servedOn', 'Actual service date', 'date'),
        field('effectiveOn', 'Actual effective service date', 'date'),
        select(
          'method',
          'Method',
          ['Post', 'Email', 'Personal service', 'Other'].map((s) => [s, s])
        ),
        field('address', 'Exact approved service address'),
        select('result', 'Outcome', [
          ['Served', 'Served'],
          ['Failed', 'Failed']
        ]),
        field('details', 'Service evidence and outcome')
      ])}
      {form(
        'Withdraw service',
        'Correct an incorrect service record',
        [
          select(
            'serviceId',
            'Service record',
            events
              .filter((e) => e.event_type === 'Service' && e.payload.result === 'Served' && !events.some((w) => w.event_type === 'Withdraw service' && w.payload.serviceId === e.id))
              .map((e) => [e.id, `${formatDateOnly(e.payload.servedOn)} — ${row.facts.recipients.find((r: Row) => r.key === e.payload.recipientKey)?.name ?? 'Earlier recipient'}`])
          ),
          field('details', 'Why this service record is incorrect')
        ],
        true
      )}
      {kind === 'Section 20' && (
        <>
          {form('Observation', 'Save an observation', [
            select('noticeId', 'Notice', noticeOptions),
            select('recipientKey', 'From', recipientOptions),
            field('receivedOn', 'Received on', 'date'),
            field('observation', 'Observation'),
            field('response', 'Response, if reviewed', 'textarea', true)
          ])}
          {form('Respond', 'Respond to an observation', [
            select(
              'observationId',
              'Observation',
              active.filter((e) => e.event_type === 'Observation').map((e) => [e.id, e.payload.observation.slice(0, 120)])
            ),
            field('response', 'Reviewed response')
          ])}
          {form('Nomination', 'Save a supplier nomination', [
            select(
              'noticeId',
              'Intention notice',
              noticeOptions.filter((_, i) => notices[i]?.payload.stage === 'Intention')
            ),
            select('recipientKey', 'Nominated by', recipientOptions),
            select('contractorId', 'Supplier', supplierOptions),
            field('receivedOn', 'Received on', 'date'),
            {
              name: 'association',
              label: 'Nominated by a recognised tenants’ association',
              type: 'checkbox'
            }
          ])}
          {form('Nomination review', 'Retain the estimate enquiry for a nominee', [
            select(
              'nominationId',
              'Nomination',
              active.filter((e) => e.event_type === 'Nomination').map((e) => [e.id, e.payload.supplierName])
            ),
            select(
              'outcome',
              'Outcome',
              ['Estimate requested', 'Estimate received', 'Unable to obtain'].map((s) => [s, s])
            ),
            field('details', 'Enquiry, response and reason for the outcome')
          ])}
          {form('Estimate', 'Retain an estimate', [
            select('contractorId', 'Supplier', supplierOptions),
            field('quoteId', 'Existing Work quote reference, if recorded', 'text', true),
            field('gross', 'Total including VAT (£)', 'text'),
            {
              name: 'independent',
              label: 'Supplier is independent of the landlord',
              type: 'checkbox'
            },
            field('details', 'Estimate description, duration, terms and any connection to the landlord')
          ])}
          {form(
            'Clear for appointment',
            'Approve consultation clearance before appointment',
            [
              select('estimateId', 'Selected estimate (optional for works under an agreement or dispensation)', estimateOptions, true),
              field('maximumContribution', 'Highest individual contribution (�)', 'text'),
              field('contributionBasis', 'Reviewed contribution calculation')
            ],
            true
          )}
          {form(
            'Award',
            'Record the reviewed supplier selection',
            [
              select(
                'estimateId',
                'Chosen estimate',
                active.filter((e) => e.event_type === 'Estimate').map((e) => [e.id, `${e.payload.supplierName} — £${e.payload.gross}`])
              ),
              field('awardedOn', 'Actual award date', 'date'),
              field('maximumContribution', 'Highest individual contribution for this award (£)', 'text'),
              field('contributionBasis', 'Reviewed contribution calculation for this award'),
              field('details', 'Reasons and response to observations')
            ],
            true
          )}
          {form('Dispensation', 'Retain tribunal dispensation', [field('details', 'Decision, scope, conditions and how conditions are satisfied')], true)}
        </>
      )}
      {form('Reopen', 'Reopen a completed review for correction', [], true)}
      {form('Complete', 'Complete the authorised review', [], true)}
      {form('Cancel', 'Cancel this consultation', [], true)}
      <section>
        <h2>History and evidence</h2>
        {events.map((e) => (
          <details key={e.id}>
            <summary>
              {e.event_type} — {formatTimestampDate(e.created_at)} — {e.actor_name ?? 'Recorded actor'}
            </summary>
            <p>{e.payload.reason}</p>
            {e.payload.observation && <p>{e.payload.observation}</p>}
            {e.payload.response && <p>{e.payload.response}</p>}
            {e.payload.details && <p>{e.payload.details}</p>}
            {e.evidence.map((d: Row) => (
              <p key={d.versionId}>
                <Link to={`/documents/${d.id}`}>Open retained supporting document</Link>
              </p>
            ))}
          </details>
        ))}
      </section>
    </>
  );
}
